import { ValidationContext } from "../hooks/useData";
import { parseDate, toBool, toNumber, toStr } from "../utils/conversion";
import { calculateAge, validateEmail } from "../utils/validate";
import { FieldDef } from "./types";


export function requiredRule(context: ValidationContext<any>, prop: string) {
	const val = context.data.getValue(prop);
    if (
		val === undefined || val === null || val === ""

		// NOTE: removed "false" from this list because that is a valid selection, e.g. "Yes/No"
		//|| val === false
	) {
        context.errors.push({ message: "Required", prop });
    }
};

export function passwordRule(context: ValidationContext<any>, prop: string) {
	const val = context.data.getValue(prop);

	if (val === undefined || val === null || val === "") {
		context.errors.push({ message: "Required", prop });
	} else {
		if (val.length < 8) {
			context.errors.push({ message: "Must be at least 8 characters", prop, display: true });
		}
	}
};

export function validDateRule(context: ValidationContext<any>, prop: string) {
	// only check if date is valid if something entered
	const val = context.data.getValue(prop);
	if (!val || val === "--") return;

	const parts = val.split("-");
	if (parts.length === 3) {
		// javascript seems to take any values and make a valid date out of it
		const year = parseInt(parts[0], 10);
		const month = parseInt(parts[1], 10) - 1; // js uses month index zero based
		const day = parseInt(parts[2], 10);

		const dt = new Date(year, month, day);
		// so check if the date month and day values still match
		if (day === dt.getDate() && month === dt.getMonth() && year === dt.getFullYear()) {
			// all good
			return;
		}
	}

	// not valid
	context.errors.push({ message: "Must be a valid date", prop, display: true });

};

export function ageRule(context: ValidationContext<any>, prop: string, options: {
	minYears?: number,
	maxYears?: number,
}) {
	// only check if date is valid if something entered
	const val = context.data.getValue(prop);
	if (!val) return;

	const dt = parseDate(val);
	if (!dt) return;

	const age = calculateAge(dt);

	const { minYears, maxYears } = options;

	if (
		minYears !== undefined && maxYears !== undefined
		&& (age < minYears || age > maxYears)
	) {
		context.errors.push({ message: `Age must be between ${minYears} and ${maxYears}`, prop, display: true})

	} else if (minYears !== undefined && age < minYears) {
		context.errors.push({ message: `Age must be at least ${minYears}`, prop, display: true})

	} else if (maxYears !== undefined && age > maxYears) {
		context.errors.push({ message: `Age must be no more than ${maxYears}`, prop, display: true})

	}
};


export function mustBeCheckedRule(context: ValidationContext<any>, prop: string) {
	const val = context.data.getValue(prop);
	if (!toBool(val)) {
		context.errors.push({ message: "Must be checked", prop, display: true });
	}
};

export function paymentMethodRule(context: ValidationContext<any>, prop: string) {
	const val = context.data.getValue(prop);
	if (!val
		|| (val && (
			!val.cardNumber
		))
	) {
		context.errors.push({ message: "Billing details required", prop, display: true });
	}
};

interface CharsLimitOptions {
	min?: number;
	max?: number;
}

export function charsLimitRule(context: ValidationContext<any>, prop: string, options: CharsLimitOptions) {
	const val: string = toStr(context.data.getValue(prop));

	const isMinError = (options.min !== undefined && options.min > 0 && (val.length < options.min));
	const isMaxError = (options.max !== undefined && options.max > 0 && (val.length > options.max));

	if (isMinError && isMaxError) {
		context.errors.push({ message: "Must be between " + options.min + " and " + options.max + " characters", prop, display: true });
	} else if (isMinError) {
		context.errors.push({ message: "Must be at least " + options.min + " characters", prop, display: true });
	} else if (isMaxError) {
		context.errors.push({ message: "Must be no more than " + options.max + " characters", prop, display: true });
	}
};

export interface RangeOptions {
	min?: number;
	max?: number;

	minMessage?: string,
	maxMessage?: string,
}

export function rangeRule(context: ValidationContext<any>, prop: string, options: RangeOptions) {
	const min = toNumber(options.min);
	const max = toNumber(options.max);
	const hasMin = !isNaN(min);
	const hasMax = !isNaN(max);

	const val: number = toNumber(context.data.getValue(prop));

	const validMin = (hasMin && (val >= min)) || !hasMin;
	const validMax = (hasMax && (val <= max)) || !hasMax;

	if (hasMin && hasMax && (val > max || val < min)) {
		context.errors.push({ message: "Must be between " + min + " and " + max, prop, display: true });
	} else {
		if (!validMin) {
			if (options.minMessage) {
				context.errors.push({ message: options.minMessage, prop, display: true });
			} else {
				context.errors.push({ message: "Must be at least " + min, prop, display: true });
			}
		}
		if (!validMax) {
			if (options.maxMessage) {
				context.errors.push({ message: options.maxMessage, prop, display: true });
			} else {
				context.errors.push({ message: "Must be no greater than " + max, prop, display: true });
			}
		}
	}
}



export function dataTypeRule(
	context: ValidationContext<any>, 
	prop: string, 
	options: {
		type: "number",
	}
) {
	const val = context.data.getValue(prop);

	if (options.type === "number") {
		if (isNaN(Number(val))) {
			context.errors.push({ message: "Must be a number", prop, display: true });
		}

	}
}

export function equalToRule(
	context: ValidationContext<any>, 
	prop: string, 
	options: {
		compareToProp: string,
	}
) {
	const val = context.data.getValue(prop);
	const valCompare = context.data.getValue(options.compareToProp);
	if (val !== valCompare) {
		context.errors.push({ message: "Must match exactly", prop, display: true });
	}
};

export function emailRule(context: ValidationContext<any>, prop: string) {
	const email = context.data.getValue(prop);

	if (!validateEmail(email)) {
		context.errors.push({ message: "Must be a valid email address", prop, display: true });
	}
}

export function validateFieldDef(context: ValidationContext<any>, def: FieldDef) {

	const options = def.options;
		
	if (options) {
		if (options.required) {
			requiredRule(context, def.prop);
		}

		if (options.maxChars || options.minChars) {
			charsLimitRule(context, def.prop, {
				min: options.minChars,
				max: options.maxChars,
			});
		}
	}

	if (def.prop === "email") {
		emailRule(context, def.prop);
	}

	if (def.prop === "password") {
		passwordRule(context, def.prop);
	}

	if (def.prop === "passwordConfirm") {
		equalToRule(context, def.prop, { compareToProp: "password" });
	}

}

export function validACNRule(context: ValidationContext<any>, prop: string, required: boolean) {
	let val = context.data.getValue(prop);
	if (!val) return;

	val = val.replace(/ /g, ""); // expecting 9 digits

	function check() {
		if ((val.length === 0) && !required) return true;
		if ((val.length !== 9) && required) return false;

		var weight = 8;
		var sum = 0;
		for (var i = 0; i < 8; i++) {
			var ival = parseInt(val.substr(i, 1), 10);
			if (isNaN(ival)) {
				return false;
			}

			var multiple = weight * ival;
			sum += multiple;
			weight--;
		}

		var result = sum / 10;
		var remainder = sum % 10;
		var remainderComplement = 10 - remainder;
		if (remainderComplement < 10) {
			result = remainderComplement;
		} else {
			result = 0;
		}

		var checksum = parseInt(val.substr(8, 1), 10);
		if (isNaN(checksum)) {
			return false;
		} else {
			return (result === checksum);
		}    
	}

	if (!check()) {
		context.errors.push({ message: "Must be a valid ACN", prop, display: true });
	}


};

